import { useState, useEffect, useRef, useCallback } from 'react';
import { listAllowedEmails, addAllowedEmail, removeAllowedEmail } from '../services/franchiseeService';
import { Link } from 'react-router-dom';
import { FaEnvelope, FaCheckCircle, FaClock, FaCalendarAlt, FaEnvelopeOpenText, FaPlus, FaTrashAlt, FaSearch, FaUser } from 'react-icons/fa';

// Helper function to format Firestore Timestamp
const formatTimestamp = (timestamp) => {
  if (!timestamp || !timestamp._seconds) return 'N/A';
  const date = new Date(timestamp._seconds * 1000);
  const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true };
  return date.toLocaleDateString('en-AU', options);
};

// Helper function to format status
const formatStatus = (status) => {
    if (!status) return 'N/A';
    return status.charAt(0).toUpperCase() + status.slice(1);
};

const PAGE_SIZE = 20;

const AllowedEmailsPage = () => {
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Search state
  const [search, setSearch] = useState('');
  const searchDebounceRef = useRef(null);

  // Pagination state — pageTokens[0] is always null (first page)
  const [pageTokens, setPageTokens] = useState([null]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  // State for Add Email Modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // State for Delete Confirmation Modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchEmails = useCallback(async (startAfterDocId, searchTerm) => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAllowedEmails({
        pageSize: PAGE_SIZE,
        startAfterDocId: startAfterDocId || null,
        search: searchTerm || ''
      });
      setAllowedEmails(data.emails || []);
      setHasNextPage(!!data.nextPageDocId);
      // Store the next page cursor into pageTokens if not already there
      if (data.nextPageDocId) {
        setPageTokens(prev => {
          const next = [...prev];
          // currentPage + 1 is the index of the next page token
          next[currentPage + 1] = data.nextPageDocId;
          return next;
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load allowed emails.');
      setAllowedEmails([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Initial load and when page/search changes
  useEffect(() => {
    fetchEmails(pageTokens[currentPage], search);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Effect to clear messages after a timeout
  useEffect(() => {
    let timer;
    if (successMessage || actionError) {
      timer = setTimeout(() => {
        setSuccessMessage(null);
        setActionError(null);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [successMessage, actionError]);

  // --- Search ---
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      // Reset to first page on new search
      setCurrentPage(0);
      setPageTokens([null]);
      setHasNextPage(false);
      fetchEmails(null, val);
    }, 500);
  };

  // --- Pagination ---
  const handlePrevPage = () => {
    if (currentPage === 0) return;
    setCurrentPage(prev => prev - 1);
  };

  const handleNextPage = () => {
    if (!hasNextPage) return;
    setCurrentPage(prev => prev + 1);
  };

  // --- Add Email Handlers ---
  const handleOpenAddModal = () => {
    setNewEmail('');
    setNewFirstName('');
    setNewLastName('');
    setActionError(null);
    setIsAddModalOpen(true);
  };

  const handleAddEmailSubmit = async (e) => {
    e.preventDefault();
    if (!newEmail) {
      setActionError("Email address cannot be empty.");
      return;
    }
    // Required because the welcome email opens with "Hi <first name>,"
    if (!newFirstName.trim()) {
      setActionError("First name cannot be empty.");
      return;
    }
    if (!newLastName.trim()) {
      setActionError("Last name cannot be empty.");
      return;
    }
    setIsAdding(true);
    setActionError(null);
    setSuccessMessage(null);

    try {
      const response = await addAllowedEmail(newEmail, newFirstName.trim(), newLastName.trim());
      // Reset to first page and refetch so the new entry is visible
      setCurrentPage(0);
      setPageTokens([null]);
      await fetchEmails(null, search);
      setSuccessMessage(response.message || `Email ${newEmail} added successfully.`);
      setIsAddModalOpen(false);
      setNewEmail('');
      setNewFirstName('');
      setNewLastName('');
    } catch (err) {
      setActionError(err.message || 'Failed to add email.');
    } finally {
      setIsAdding(false);
    }
  };

  // --- Delete Email Handlers ---
  const handleDeleteClick = (email) => {
    setEmailToDelete(email);
    setActionError(null);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!emailToDelete) return;

    setIsDeleting(true);
    setActionError(null);
    setSuccessMessage(null);

    try {
      const response = await removeAllowedEmail(emailToDelete);
      setAllowedEmails(prevEmails => prevEmails.filter(e => e.email !== emailToDelete));
      setSuccessMessage(response.message || `Email ${emailToDelete} removed successfully.`);
      setIsDeleteModalOpen(false);
      setEmailToDelete(null);
    } catch (err) {
      setActionError(err.message || 'Failed to remove email.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-jims-cyan-dark flex items-center">
          <FaEnvelopeOpenText className="mr-3" /> Allowed Franchisee Emails
        </h1>
        <div className="flex items-center space-x-4">
           <button
              onClick={handleOpenAddModal}
              className="bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300"
           >
              <FaPlus className="mr-2" />
              Add New Email
           </button>
           <Link to="/franchisees" className="text-jims-cyan-dark hover:text-jims-cyan underline">
             Back to Franchisees
           </Link>
        </div>
      </div>

      {/* Search Input */}
      <div className="mb-4 relative max-w-md">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search by email..."
          value={search}
          onChange={handleSearchChange}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan text-sm"
        />
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
          {successMessage}
        </div>
      )}
      {/* Action Error Message */}
      {actionError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
          Error: {actionError}
        </div>
      )}

      {loading && <p className="text-center text-gray-500 py-10">Loading emails...</p>}
      {error && !loading && <p className="text-center text-red-500 bg-red-100 p-3 rounded mb-4">Error: {error}</p>}

      {!loading && !error && (
        <>
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            {allowedEmails.length === 0 ? (
              <p className="text-center text-gray-500 p-6">No allowed emails found.</p>
            ) : (
              <table className="min-w-full leading-normal">
                <thead>
                  <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm">
                    <th className="px-5 py-3 border-b-2 border-gray-200"><FaEnvelope className="inline mr-1" /> Email Address</th>
                    <th className="px-5 py-3 border-b-2 border-gray-200"><FaUser className="inline mr-1" /> First Name</th>
                    <th className="px-5 py-3 border-b-2 border-gray-200"><FaUser className="inline mr-1" /> Last Name</th>
                    <th className="px-5 py-3 border-b-2 border-gray-200"><FaCheckCircle className="inline mr-1" /> Status</th>
                    <th className="px-5 py-3 border-b-2 border-gray-200"><FaCalendarAlt className="inline mr-1" /> Date Added</th>
                    <th className="px-5 py-3 border-b-2 border-gray-200"><FaClock className="inline mr-1" /> Date Registered</th>
                    <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allowedEmails.map((item) => (
                    <tr key={item.email} className="hover:bg-gray-50">
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">{item.email}</p>
                      </td>
                      {/* Entries predating this field, and any added by the bulk script, have no name */}
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">{item.firstName || '—'}</p>
                      </td>
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">{item.lastName || '—'}</p>
                      </td>
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          item.status === 'registered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {formatStatus(item.status)}
                        </span>
                      </td>
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">{formatTimestamp(item.addedAt)}</p>
                      </td>
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">{formatTimestamp(item.registeredAt)}</p>
                      </td>
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm text-center">
                        <button
                          onClick={() => handleDeleteClick(item.email)}
                          className="text-red-500 hover:text-red-700 transition duration-300 disabled:opacity-50"
                          aria-label={`Delete email ${item.email}`}
                          disabled={isDeleting}
                        >
                          <FaTrashAlt />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition duration-150 text-sm"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">Page {currentPage + 1}</span>
            <button
              onClick={handleNextPage}
              disabled={!hasNextPage}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition duration-150 text-sm"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Add Email Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Add New Allowed Email</h3>
            <form onSubmit={handleAddEmailSubmit}>
              <div className="mb-4">
                <label htmlFor="new-email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  type="email"
                  id="new-email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                  required
                  placeholder="franchisee@example.com"
                />
              </div>
              {/* Both names are required: the welcome email opens with "Hi <first name>," */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label htmlFor="new-first-name" className="block text-sm font-medium text-gray-700">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="new-first-name"
                    value={newFirstName}
                    onChange={(e) => setNewFirstName(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    required
                    placeholder="Martin"
                  />
                </div>
                <div>
                  <label htmlFor="new-last-name" className="block text-sm font-medium text-gray-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="new-last-name"
                    value={newLastName}
                    onChange={(e) => setNewLastName(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    required
                    placeholder="Smith"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                The franchisee is emailed a "How to use Loope" guide addressed to their first name.
              </p>
               {actionError && !successMessage && (
                 <p className="text-sm text-red-600 mb-3">{actionError}</p>
               )}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition duration-150"
                  disabled={isAdding}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-jims-cyan text-white rounded hover:bg-jims-cyan-dark transition duration-150 disabled:opacity-50"
                  disabled={isAdding}
                >
                  {isAdding ? 'Adding...' : 'Add Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-2">Confirm Deletion</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to remove the email <span className="font-semibold">{emailToDelete}</span> from the allowed list?
            </p>
             {actionError && !successMessage && (
               <p className="text-sm text-red-600 mb-3">{actionError}</p>
             )}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition duration-150"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition duration-150 disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AllowedEmailsPage;
